import { useState, useEffect, useMemo, useRef, PropsWithChildren } from "react";
import { useQuery } from "@apollo/client";
import { USER_FEATURE_FLAG_QUERY } from "./queries";
import { initializeFeatures, FlagRecord, FeatureFlagContext } from "@/hooks/useFeatureFlags";
import { fetchActiveFlagKeys } from "@/hooks/useFeatureFlags/api";
import type { FlagKey } from "@/hooks/useFeatureFlags/flags";

type ActiveFlagKeysPrefetch = { request: Promise<FlagKey[]>; abort: () => void };

const startActiveFlagKeysPrefetch = (): ActiveFlagKeysPrefetch => {
  const controller = new AbortController();
  const request = fetchActiveFlagKeys(controller.signal);
  // A speculative failure is retried by initializeFeatures; keep it from surfacing as an unhandled rejection.
  request.catch(() => {});
  return { request, abort: () => controller.abort() };
};

// This provider remounts whenever the signed-in or assumed user changes (see providers.tsx keys), so a
// prefetch held in a ref belongs to exactly one session and is never read by another user's provider.
const FeatureFlagProvider = ({ children }: PropsWithChildren<{}>) => {
  const [featureFlags, setFeatureFlags] = useState<FlagRecord>({});
  const [loaded, setLoaded] = useState(false);
  const { data, loading } = useQuery(USER_FEATURE_FLAG_QUERY);
  const prefetchRef = useRef<ActiveFlagKeysPrefetch | undefined>(undefined);
  const mountedRef = useRef(false);

  // The active flag list does not depend on the user, so start it with the user query instead of after it.
  useEffect(() => {
    mountedRef.current = true;
    if (!prefetchRef.current) {
      prefetchRef.current = startActiveFlagKeysPrefetch();
    }
    const prefetch = prefetchRef.current;

    return () => {
      mountedRef.current = false;
      // Strict Mode replays cleanup and setup back to back and must keep one request; abort only if no setup follows.
      queueMicrotask(() => {
        if (!mountedRef.current && prefetchRef.current === prefetch) {
          prefetch.abort();
          prefetchRef.current = undefined;
        }
      });
    };
  }, []);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!data) {
      prefetchRef.current?.abort();
      prefetchRef.current = undefined;
      setLoaded(true);
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    const prefetch = prefetchRef.current;

    const fetchFeatureFlags = async () => {
      try {
        const flags = await initializeFeatures(data.me, { activeFlagKeysRequest: prefetch?.request, signal });

        if (!signal.aborted && flags) {
          setFeatureFlags(flags);
        }
      } finally {
        if (!signal.aborted) {
          if (prefetchRef.current === prefetch) {
            prefetchRef.current = undefined;
          }
          setLoaded(true);
        }
      }
    };

    fetchFeatureFlags();

    return () => {
      controller.abort();
    };
  }, [data, loading]);

  const value = useMemo(() => ({ flags: featureFlags, loaded }), [featureFlags, loaded]);

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};

export default FeatureFlagProvider;
